import React, { useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Sort as SortIcon } from '@mui/icons-material';
import ActionHelpTooltip from '../common/ActionHelpTooltip';
import type { HelpValue } from '../common/helpModel';
import { borderRadius, getFieldBorderColor } from '../../../theme/theme';
import { releaseFocusBeforeOverlay } from '../../lib/utils/accessibilityUtils';

interface TableHeaderFieldOption {
  value: string;
  label: string;
}

interface TableHeaderFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  selectOptions?: TableHeaderFieldOption[];
  ariaLabel?: string;
  onSort?: () => void;
  sortDirection?: 'asc' | 'desc' | null;
  fullWidth?: boolean;
  disabled?: boolean;
  help?: HelpValue;
  /** Help tooltip for the sort button. */
  sortHelp?: HelpValue;
  tooltipPlacement?: 'bottom' | 'bottom-start' | 'top' | 'top-start' | 'right';
  inputHeight?: number;
  /** Optional control rendered inside the field, before the sort button. */
  trailingAdornment?: React.ReactNode;
}

const TableHeaderField: React.FC<TableHeaderFieldProps> = ({
  label,
  value,
  onChange,
  selectOptions,
  ariaLabel,
  onSort,
  sortDirection = null,
  fullWidth = true,
  disabled = false,
  help,
  sortHelp,
  tooltipPlacement = 'top',
  inputHeight = 22,
  trailingAdornment,
}) => {
  const theme = useTheme();
  const isSelect = Array.isArray(selectOptions);
  // Controls with their own help tooltips (advanced column filter, sort) must
  // not surface the field help while hovered.
  const [fieldActionsHovered, setFieldActionsHovered] = useState(false);
  const fieldBorderColor = getFieldBorderColor(theme);
  const isNormalField = inputHeight >= 28;
  const fieldFontSize = isNormalField ? '0.6875rem' : '0.56rem';
  const labelFontSize = isNormalField ? '0.6875rem' : '0.54rem';
  const shrinkLabelFontSize = isNormalField ? '0.625rem' : '0.48rem';
  const sortButtonSize = isNormalField ? 24 : 20;
  const sortIconSize = isNormalField ? '0.875rem' : '0.68rem';
  const fieldRadius = `${borderRadius.sm}px`;
  const fieldActiveBg = alpha(theme.palette.background.default, 0.2);
  const inputPy = isNormalField ? 0.5 : 0.22;

  const sortButton = (
    <IconButton
      size="small"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onSort?.();
      }}
      aria-label={`Sort by ${label}`}
      sx={{
        width: sortButtonSize,
        height: sortButtonSize,
        p: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        borderRadius: fieldRadius,
        backgroundColor: 'transparent',
        color: sortDirection ? theme.palette.primary.main : theme.palette.text.secondary,
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
        '&:focus-visible': {
          backgroundColor: alpha(theme.palette.primary.main, 0.12),
        },
      }}
    >
      <SortIcon
        sx={{
          fontSize: sortIconSize,
          transform: sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
        }}
      />
    </IconButton>
  );

  const field = (
    <TextField
      size="small"
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      select={isSelect}
      fullWidth={fullWidth}
      variant="outlined"
      disabled={disabled}
      SelectProps={isSelect ? { onOpen: releaseFocusBeforeOverlay } : undefined}
      inputProps={{ 'aria-label': ariaLabel || label }}
      InputLabelProps={{
        shrink: value.length > 0 ? true : undefined,
      }}
      InputProps={!isSelect && (onSort || trailingAdornment) ? {
        endAdornment: (
          <InputAdornment
            position="end"
            sx={{
              alignSelf: 'stretch',
              height: '100%',
              maxHeight: 'none',
              ml: isNormalField ? 0.25 : 0,
              mr: isNormalField ? -0.25 : 0,
              gap: 0.25,
            }}
          >
            {trailingAdornment ? (
              <Box
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'center' }}
                onMouseEnter={() => setFieldActionsHovered(true)}
                onMouseLeave={() => setFieldActionsHovered(false)}
              >
                {trailingAdornment}
              </Box>
            ) : null}
            {onSort ? (
              sortHelp ? (
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'center' }}
                  onMouseEnter={() => setFieldActionsHovered(true)}
                  onMouseLeave={() => setFieldActionsHovered(false)}
                >
                  <ActionHelpTooltip help={sortHelp} placement="top">
                    {sortButton}
                  </ActionHelpTooltip>
                </Box>
              ) : (
                sortButton
              )
            ) : null}
          </InputAdornment>
        ),
      } : undefined}
      sx={{
        '& .MuiOutlinedInput-root': {
          height: inputHeight,
          fontSize: fieldFontSize,
          backgroundColor: alpha(theme.palette.background.default, 0.12),
          borderRadius: fieldRadius,
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
          '& fieldset': {
            borderColor: fieldBorderColor,
            borderRadius: fieldRadius,
          },
          '&:hover': {
            backgroundColor: fieldActiveBg,
          },
          '&:hover fieldset': {
            borderColor: fieldBorderColor,
          },
          '&.Mui-focused': {
            backgroundColor: fieldActiveBg,
            boxShadow: `inset 0 0 0 2px ${alpha(
              theme.palette.divider,
              theme.palette.mode === 'light' ? 0.22 : 0.18
            )}`,
          },
          '&.Mui-focused fieldset': {
            borderColor: fieldBorderColor,
            borderWidth: 1,
          },
        },
        '& .MuiInputLabel-root': {
          fontSize: labelFontSize,
          color: alpha(theme.palette.text.secondary, 0.84),
          // Keep the floating label clear of the sort adornment: the label is
          // translated ~14px right, so reserve that offset plus the button.
          maxWidth: !isSelect && onSort ? `calc(100% - ${sortButtonSize + 18}px)` : 'calc(100% - 4px)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        // MUI's small outlined label offset assumes a 40px input; the compact
        // heights here are shorter, so vertically centre the resting label.
        '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
          top: '50%',
          transform: 'translate(14px, -50%)',
        },
        '& .MuiInputLabel-shrink': {
          fontSize: shrinkLabelFontSize,
        },
        '& .MuiOutlinedInput-input': {
          py: inputPy,
          px: isNormalField ? 1.75 : 0.46,
        },
        '& .MuiSelect-select': {
          py: inputPy,
          pr: 2.1,
        },
      }}
    >
      {isSelect &&
        selectOptions?.map((option) => (
          <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.62rem' }}>
            {option.label}
          </MenuItem>
        ))}
    </TextField>
  );

  if (!help) {
    return field;
  }

  // Column help rides on the field itself — no detached "?" icons in the header.
  return (
    <ActionHelpTooltip
      help={help}
      placement={tooltipPlacement}
      fullWidth
      wrapperSx={{ minWidth: 0 }}
      suppressed={fieldActionsHovered}
    >
      {field}
    </ActionHelpTooltip>
  );
};

export default React.memo(TableHeaderField);
